/**
 * The wireless talks about you (docs/UI.md, "The radio's news desk"): the stations' news, travel and DJ
 * links carry short bulletins about what the player has been up to — a car taken in Coalgate, a chase
 * through the Parade, a mayhem challenge's damage, a job's aftermath — and, online, the shard's world
 * events and turf news. Text only: the voices on the radio are formant "speech" without words, so the
 * bulletin is what the ticker shows while that item is on air.
 *
 * Plain period voice, original wording, never a player's name (online: districts and colours only). Rate-
 * limited: one bulletin at most every `NEWS_GAP` seconds, each piece of news aired once and forgotten after
 * `NEWS_SHELF_LIFE`. The stations' running orders are untouched (still deterministic and seeded): the
 * desk is only asked, once, as an item comes on air.
 *
 * Pure (no audio, no game): the audio system feeds it events and the radio asks it for a line.
 */
import type { MayhemKind } from '../../activities/mayhem';
import { modifierBulletin, parseModifier } from '../../../shared/week';

/** Seconds between two bulletins, whatever the station. */
export const NEWS_GAP = 75;
/** Seconds a piece of news stays worth reading out. */
export const NEWS_SHELF_LIFE = 300;
/** At most this many stories wait (the oldest drops). */
export const NEWS_QUEUE = 6;

export type NewsStory =
  /** A car taken; `carjack`: the driver was pulled out of it. */
  | { kind: 'theft'; model: string; colour: string; district: string; carjack?: boolean }
  /** A chase on; `level` 3+ is the big one (roadblocks, every car the station has). */
  | { kind: 'chase'; district: string; level?: number }
  | { kind: 'gotAway'; district: string }
  | { kind: 'arrest'; district: string }
  /** A police car forced off the road and out of the chase. */
  | { kind: 'takedown'; district: string }
  /** A job's aftermath. `id` (a mission id) gets the story's own bulletin; without one, something vaguer. */
  | { kind: 'job'; district: string; id?: string }
  /** A night race on the quay. */
  | { kind: 'race'; district: string; won: boolean }
  | { kind: 'mayhem'; what: MayhemKind; district: string; done: number; tier: number }
  | { kind: 'world'; text: string };

/** Which radio items can carry a bulletin, and the label the ticker gives it. */
export type NewsSlot = 'news' | 'traffic' | 'link';

export const SLOT_LABEL: Record<NewsSlot, string> = { news: 'News', traffic: 'Travel news', link: 'Newsflash' };

/** Travel news only reads car and road stories; the news and a DJ's link will read anything. */
export function fitsSlot(story: NewsStory, slot: NewsSlot): boolean {
  if (slot !== 'traffic') return true;
  return story.kind === 'theft' || story.kind === 'chase' || story.kind === 'gotAway' || story.kind === 'takedown' || story.kind === 'race' || (story.kind === 'mayhem' && story.what !== 'heavies');
}

const pick = <T>(list: readonly T[], n: number): T => list[((n % list.length) + list.length) % list.length]!;

const a = (word: string): string => (/^[aeiou]/i.test(word) ? `an ${word}` : `a ${word}`);

/**
 * The words for a story. `n` varies the wording (the story's running number: deterministic). Every
 * bulletin is one or two sentences under 140 characters: the reader's dry Evening Post voice, "is said to
 * be", "enquiries continue", never a name (docs/WRITING.md §2).
 */
export function bulletinText(s: NewsStory, n: number): string {
  switch (s.kind) {
    case 'theft': {
      const car = a(`${s.colour} ${s.model}`);
      if (s.carjack)
        return pick(
          [
            `A driver was pulled from ${car} in ${s.district} and left on the kerb. Police want a word with whoever drove off.`,
            `${cap(car)} was taken from its owner at the lights in ${s.district}. He is said to be shaken, and short of a car.`,
            `Bold as brass in ${s.district}: a man took ${car} with the driver still in it. The driver is no longer in it.`,
          ],
          n,
        );
      return pick(
        [
          `${cap(car)} was taken from a street in ${s.district}. Police ask anyone who saw it to come forward.`,
          `Motorists in ${s.district}: ${car} has been reported stolen. Police have a description.`,
          `Another car gone in ${s.district}: ${car}. The owner is said to be livid.`,
          `${cap(car)} left a kerb in ${s.district} without its owner this evening. Police are not hopeful.`,
          `Residents of ${s.district} are asked to lock their cars after ${car} went missing. Again.`,
        ],
        n,
      );
    }
    case 'chase':
      if ((s.level ?? 2) >= 3)
        return pick(
          [
            `Roadblocks are going up round ${s.district}. Police want one driver, and they want him badly.`,
            `Every police car in the borough is said to be in ${s.district} tonight. Drivers: find another way home.`,
            `A major pursuit through ${s.district}. The station has called in cars from across the river.`,
          ],
          n,
        );
      return pick(
        [
          `Police cars are out in force round ${s.district} after a chase. Drivers are asked to keep clear.`,
          `Reports of a police chase through ${s.district}. Avoid the area if you can.`,
          `Sirens in ${s.district}: police are after a driver who would not stop.`,
          `A car is being chased through ${s.district} at speeds the police describe as "unwise".`,
        ],
        n,
      );
    case 'gotAway':
      return pick(
        [
          `Police say the driver they chased through ${s.district} got clean away. Enquiries continue.`,
          `The chase in ${s.district} is over, and the police have nothing to show for it.`,
          `The driver police followed through ${s.district} has not been found. A sergeant said he "knew the alleys".`,
          `Quiet again in ${s.district}. The car the police were after was last seen turning a corner, and that was that.`,
        ],
        n,
      );
    case 'arrest':
      return pick(
        [
          `A man was arrested in ${s.district} after a disturbance. He is helping police with their enquiries.`,
          `An arrest in ${s.district}. Police say a man will be up before the magistrates in the morning.`,
          `Police in ${s.district} have a man in the cells tonight. They describe him as "known to us".`,
          `One arrest in ${s.district} this evening. The desk sergeant called it "a quiet night, for once".`,
        ],
        n,
      );
    case 'takedown':
      return pick(
        [
          `A police car left the road in ${s.district} during a chase. The officers are bruised; the car is scrap.`,
          `Drivers in ${s.district}: a police car is on its side by the kerb. The man it was after is not.`,
          `Another patrol car written off in ${s.district}. The Chief Constable is said to want a word with somebody.`,
          `A chase through ${s.district} ended with a police car in a shop front. Nobody hurt, except the shop.`,
        ],
        n,
      );
    case 'job':
      return jobText(s, n);
    case 'race':
      return s.won
        ? pick(
            [
              `Residents of ${s.district} were woken by what sounded like a motor race. Police say it probably was.`,
              `Loud cars on the quay again overnight, and somebody is said to have won a good deal of money.`,
              `A line of cars took ${s.district} at speed in the small hours. Police arrived to find only tyre marks.`,
            ],
            n,
          )
        : pick(
            [
              `More racing on the back streets of ${s.district} last night. One driver is said to have lost a week's wages on it.`,
              `Illegal racing in ${s.district}: police are "aware". So, say residents, is everyone with a window.`,
            ],
            n,
          );
    case 'mayhem':
      return mayhemText(s, n);
    default:
      return s.text;
  }
}

/**
 * A job's aftermath, as the paper hears it: the story jobs by id (what the borough saw of them, never what
 * you did), the export orders, the car jobs, and a vaguer line for a street job.
 */
function jobText(s: Extract<NewsStory, { kind: 'job' }>, n: number): string {
  const d = s.district;
  switch (s.id) {
    case 'borrowed-time':
      return pick(
        [
          'A red sports car belonging to a well-known Parade businessman was taken from outside a club. He is said to be furious.',
          'A nightclub owner on The Parade has lost his new red car from the kerb outside. His staff say they saw nothing.',
        ],
        n,
      );
    case 'wet-paper':
      return pick(
        [
          'Police followed a car from the railway arches to the docks last night and lost it. Dockers say it was raining.',
          'A tip-off at the arches in Cinder Yards came to nothing: police found a damp yard and no parcel.',
        ],
        n,
      );
    case 'gullet-job':
      return pick(
        [
          'Thieves emptied a bonded warehouse at Gullet Basin last night. Customs are counting; the owners are not talking.',
          'A green van seen leaving Bonded Warehouse No. 3 has been found abandoned under the arches, empty.',
        ],
        n,
      );
    case 'one-armed-bandits':
      return pick(
        [
          'A van full of gambling machines was wrecked outside a Basin Row public house by a man with a cricket bat.',
          'Landlords on the Basin are said to be quietly pleased after a delivery of fruit machines met a cricket bat.',
        ],
        n,
      );
    case 'the-collection':
      return pick(
        [
          'Police raided three betting shops in the borough last night and found three empty tills. CID declined to comment.',
          'Three raids on bookmakers across the borough netted nothing. One detective is said to be "livid".',
        ],
        n,
      );
    case 'last-orders':
      return pick(
        [
          'A senior Low Borough detective is said to be helping colleagues with their enquiries. The Post has the story tomorrow.',
          'Gunfire was reported outside a Parade nightclub last night. A black car at the kerb has lost its strongbox.',
        ],
        n,
      );
    case 'export-order':
      return pick(
        [
          `Another car has left ${d} for the Continent, say the harbour police, who were not asked to stamp its papers.`,
          `A car taken in ${d} is believed to be on a boat by now. The owner has been offered a bus timetable.`,
          `Customs at the Basin are asking how many cars leave the borough on the night tide. Nobody has a number.`,
        ],
        n,
      );
    case 'car-job-wedding':
      return 'A bride arrived at St. Brannoc’s on time in a car driven by a stranger. The hired driver was found in a public house.';
    case 'car-job-keys':
      return 'A police bait car left on Kiln Road was taken, chased and found in a lockup without its radio. The police are not laughing.';
    case 'car-job-flit':
      return 'A family on Bell Street left under cover of darkness with their furniture. Their landlord has reported a lorry.';
    case 'car-job-tide':
      return 'A minicab was seen doing a fair speed down Dock Road. Its passenger made the tide, and the quay made way.';
    case 'car-job-ices':
      return 'Children by Kiln Common report the ice-cream van was driven by somebody new. The ices, they say, were fine.';
    case 'car-job-wreaths':
      return 'The Abbott wake at the Brickmakers Arms went ahead with its wreaths. The undertaker is said to be mystified.';
    default:
      return pick(
        [
          `Police are looking into an incident in ${d}. No arrests so far.`,
          `Something has gone on in ${d}, and the police would like to know what. Anyone with information should call the station.`,
          `A disturbance in ${d} this evening; nobody is saying what. Police say they have "a fair idea".`,
          `Reports of a parcel changing hands in ${d}. The police would like to know what was in it. So would we.`,
        ],
        n,
      );
  }
}

function mayhemText(s: Extract<NewsStory, { kind: 'mayhem' }>, n: number): string {
  const many = s.done <= 1 ? 'one' : s.done < 5 ? 'a few' : s.done < 10 ? 'several' : 'a dozen or more';
  const bad = s.tier >= 3 ? ' Police call it the worst yet.' : '';
  switch (s.what) {
    case 'heavies':
      return pick(
        [
          `A fracas in ${s.district}: ${many} men treated for cuts and bruises. Nobody saw a thing.${bad}`,
          `Trouble outside a club in ${s.district}: ${many} doormen knocked about. Nobody is pressing charges.${bad}`,
          `${cap(many)} men in good suits were helped to the infirmary from ${s.district}. They fell over, they say.${bad}`,
        ],
        n,
      );
    case 'motors':
      return pick(
        [
          `${cap(many)} cars smashed up in ${s.district}. They are said to belong to a well-known family.${bad}`,
          `Vandals in ${s.district}: ${many} parked cars wrecked in a matter of minutes.${bad}`,
          `${cap(many)} claret cars in ${s.district} will not be driven again. Their owners are not talking.${bad}`,
        ],
        n,
      );
    default:
      return pick(
        [
          `Council workmen are clearing up in ${s.district} after a car ran amok along the pavements.${bad}`,
          `Bins, bollards and crates all over the road in ${s.district}. Drivers, take care.${bad}`,
          `The council has run out of cones after a night in ${s.district}. A spokesman called it "a bad one".${bad}`,
        ],
        n,
      );
  }
}

function cap(t: string): string {
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Online world news (server events and turf news) as the desk reads it: its own wording from the kind,
 * the stage and the district only — never the server's subject line, which may carry a firm's name.
 */
export function worldNewsText(
  w: { source: 'event' | 'territory' | 'week'; kind: string; stage: string; district: string; districtId?: string; crackdown?: boolean; grade?: number; you?: boolean },
  n: number,
): string | null {
  const d = w.district;
  if (w.crackdown) return `Extra police in ${d} tonight. Expect stops and searches.`;
  if (w.source === 'week') {
    // The Kessmouth week (WP-X5): the modifier from its id (and district / grade), the honours without a name.
    if (w.kind === 'modifier') {
      const m = parseModifier(0, w.stage, JSON.stringify({ district: w.districtId, grade: w.grade }));
      return m ? `This week in Kessmouth: ${modifierBulletin(m)}` : null;
    }
    if (w.kind === 'honours') return w.you ? 'The Evening Post has the week’s honours, and word is one of them has your name on it.' : pick(['The Evening Post prints the week’s honours tonight: the firms that did best in Kessmouth are in the book for good.', 'A new week, and last week’s honours are written up. Some firms will be pleased with themselves.'], n);
    return null;
  }
  if (w.source === 'territory') {
    if (w.kind === 'contest') return pick([`Word is two firms are squaring up over ${d}. Residents are keeping their doors shut.`, `Talk of trouble brewing in ${d} tonight. The police say they are aware.`], n);
    if (w.kind === 'taken') return `A change of management in ${d}, they say. The police are not commenting.`;
    return null;
  }
  const live = w.stage === 'announced' || w.stage === 'live';
  switch (w.kind) {
    case 'shipment':
      return live ? `Dockers report a boat in at the Basin. The harbour police are said to be watching.` : w.stage === 'over' ? `The quay at the Basin is quiet again after a busy night.` : null;
    case 'rarecar':
      return live ? `Police have been told to look out for an unusual motor parked in ${d}.` : w.stage === 'over' ? `That unusual motor is gone from ${d}. Nobody saw a thing, of course.` : null;
    case 'warehouse':
      return live ? `Talk of a stash in a yard in ${d}, and more than one firm after it.` : w.stage === 'over' ? `Things have quietened down at the yard in ${d}.` : null;
    default:
      return null;
  }
}

interface Queued {
  story: NewsStory;
  at: number;
  n: number;
}

/**
 * The desk: stories in, at most one bulletin out every `NEWS_GAP` seconds. `now` is any steady clock in
 * seconds (the audio system passes the game's sim time).
 */
export class NewsDesk {
  private readonly queue: Queued[] = [];
  private lastAired = -Infinity;
  private seq = 0;

  /** A story happened at `now`. A repeat of one still waiting (same words) replaces it. */
  note(story: NewsStory, now: number): void {
    const n = this.seq++;
    const key = bulletinText(story, 0);
    const i = this.queue.findIndex((q) => bulletinText(q.story, 0) === key);
    if (i >= 0) this.queue.splice(i, 1);
    this.queue.push({ story, at: now, n });
    while (this.queue.length > NEWS_QUEUE) this.queue.shift();
  }

  /** Stories waiting (fresh ones only). */
  pending(now: number): number {
    this.expire(now);
    return this.queue.length;
  }

  /**
   * An item that can carry a bulletin has come on air: the freshest story that fits it, or null (too soon
   * after the last one, or nothing to say). The story is used up.
   */
  take(slot: NewsSlot, now: number): string | null {
    this.expire(now);
    if (now - this.lastAired < NEWS_GAP) return null;
    for (let i = this.queue.length - 1; i >= 0; i--) {
      const q = this.queue[i]!;
      if (!fitsSlot(q.story, slot)) continue;
      this.queue.splice(i, 1);
      this.lastAired = now;
      return bulletinText(q.story, q.n);
    }
    return null;
  }

  clear(): void {
    this.queue.length = 0;
    this.lastAired = -Infinity;
  }

  private expire(now: number): void {
    while (this.queue.length && now - this.queue[0]!.at > NEWS_SHELF_LIFE) this.queue.shift();
  }
}

/** A paint colour (hex) as a word a newsreader would use. */
export function colourName(hex: number): string {
  const r = ((hex >> 16) & 255) / 255;
  const g = ((hex >> 8) & 255) / 255;
  const b = (hex & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const s = max === min ? 0 : l > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
  if (s < 0.18) return l > 0.82 ? 'white' : l > 0.6 ? 'silver' : l > 0.25 ? 'grey' : 'black';
  let h = 0;
  if (max === r) h = ((g - b) / (max - min) + 6) % 6;
  else if (max === g) h = (b - r) / (max - min) + 2;
  else h = (r - g) / (max - min) + 4;
  h *= 60;
  if (h < 15 || h >= 340) return l < 0.3 ? 'maroon' : 'red';
  if (h < 40) return l < 0.35 ? 'brown' : 'orange';
  if (h < 65) return l > 0.7 ? 'cream' : l < 0.35 ? 'brown' : 'yellow';
  if (h < 170) return 'green';
  if (h < 260) return l < 0.3 ? 'navy' : 'blue';
  return 'purple';
}
